import { cloneDeep } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { METRIC_KEY } from '@commons/constants/metricKey';
import { ratio, relativeRealFromTheoreticalRatio } from '@commons/utils/math';

import accountService from '@services/account';
import orderService from '@services/order';

export const fetchLocationsByAccountId = async (accountId, showMessage) => {
  try {
    const locations = await accountService.getLocationsOfAccount(accountId);

    return locations;
  } catch (err) {
    showMessage(i18next.t('HOME.ACTIVITY_REPORT_FETCH_LOCATIONS_FAILURE'), 'error');

    return [];
  }
};

const _formatRow = (dataByCategory, category, metricProperty) => {
  const { stats, entities } = dataByCategory[category];

  const formattedEntities = entities.map(({ id, name, unit, stats }) => ({
    id,
    name,
    unit,
    ...stats[metricProperty],
  }));

  return {
    name: category,
    ...stats[metricProperty],
    childItems: formattedEntities,
  };
};

export const fetchAnalytics = async (
  storeIds,
  startDate,
  endDate,
  metrics,
  { withNonSalesConsumption = true } = {},
) => {
  const { total, dataByCategory, turnoverByStoreId, lossByStoreId } =
    await orderService.getOrdersAnalyticsByCategory(
      storeIds,
      startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      { withNonSalesConsumption },
    );

  const formattedTotalRow = metrics.reduce((result, { key }) => {
    result[key] = { ...total[key], name: i18next.t('GENERAL.TOTAL').toUpperCase(), isFixed: true };

    return result;
  }, {});

  const formattedData = metrics.reduce((result, { key }) => {
    result[key] = Object.keys(dataByCategory).map((category) =>
      _formatRow(dataByCategory, category, key),
    );

    return result;
  }, {});

  return { lossByStoreId, turnoverByStoreId, data: formattedData, total: formattedTotalRow };
};

export const fetchInventoryDateOfStoreByDates = async (
  storeIds,
  startMonth,
  endMonth,
  userTimezone,
  showMessage,
) => {
  const data = await orderService.getInventoryForStores(
    storeIds,
    startMonth,
    endMonth,
    showMessage,
    'stock',
  );

  const formattedDate = { startDate: [], endDate: [] };

  if (data.list.length) {
    // For more explanation see Ticket (DEEP-1167) => https://deepsightprod.atlassian.net/jira/software/projects/DEEP/boards/12?assignee=60a6181d5998a60068872119&selectedIssue=DEEP-1167
    // Cas#1 stockConvention = start & Cas #2 stockConvention = end
    // stockConvention choose to be start if stockConvention is `null`
    if (data.list[0].lnkStoreInventorylistrel.stockConvention === 'end') {
      formattedDate.startDate = data.dates.map((item) =>
        moment
          .tz(item, userTimezone)
          .add(1, 'day')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );
      formattedDate.endDate = data.dates.map((item) =>
        moment.tz(item, userTimezone).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );
    } else {
      formattedDate.startDate = data.dates.map((item) =>
        moment.tz(item, userTimezone).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );
      formattedDate.endDate = data.dates.map((item) =>
        moment
          .tz(item, userTimezone)
          .subtract(1, 'day')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );
    }
  }

  return formattedDate;
};

const INITIAL_VALUES = {
  foodCost: {
    order: null,
    rushOrder: null,
    orderTotal: null,
    transferIn: null,
    transferOut: null,
    transferTotal: null,
    finalStock: null,
    initialStock: null,
    stockVariation: null,
    realConsumption: null,
    gapFromTheoretical: null,
  },
  loss: null,
  inventoryShrinkage: null,
  theoreticalConsumption: null,
};

/**
 * Compute the total row of the analytics data
 * Same method as getAnalyticsByCategoryStockEvents service in API codebase
 *
 * @param {Object} dataByCategory - The analytics data by category
 * @param {number} turnoverByStoreIdTotal - The total turnover by store id
 * @param {Array} formattedMetrics - The formatted metrics
 */
export const computeAndFormatTotalMetrics = (
  dataByCategory,
  turnoverByStoreIdTotal,
  formattedMetrics,
) => {
  const totalResult = Object.keys(dataByCategory).reduce(
    (result, category) => {
      const data = dataByCategory[category].stats;

      Object.values(METRIC_KEY).forEach((metric) => {
        const foodCostMetrics = data[metric].foodCost;

        for (const property of Object.keys(INITIAL_VALUES.foodCost)) {
          if (['initialStock', 'finalStock'].includes(property)) {
            continue;
          }

          if (!!foodCostMetrics && foodCostMetrics[property] != null) {
            result[metric].foodCost[property] += foodCostMetrics[property];
          }
        }

        if (
          !!foodCostMetrics &&
          foodCostMetrics.finalStock != null &&
          foodCostMetrics.initialStock != null
        ) {
          result[metric].foodCost.initialStock += foodCostMetrics.initialStock;
          result[metric].foodCost.finalStock += foodCostMetrics.finalStock;

          // re-assign stockVariation so we always have the correct value
          result[metric].foodCost.stockVariation =
            result[metric].foodCost.finalStock - result[metric].foodCost.initialStock;
        }

        if (data[metric].inventoryShrinkage) {
          result[metric].inventoryShrinkage += data[metric].inventoryShrinkage;
        }

        if (data[metric].loss != null) {
          result[metric].loss += data[metric].loss;
        }

        if (data[metric].theoreticalConsumption != null) {
          result[metric].theoreticalConsumption += data[metric].theoreticalConsumption;
        }
      });

      return result;
    },
    {
      [METRIC_KEY.TURNOVER]: cloneDeep(INITIAL_VALUES),
      [METRIC_KEY.UNIT]: cloneDeep(INITIAL_VALUES),
    },
  );

  const realConsumptionTot = totalResult[METRIC_KEY.UNIT].foodCost.realConsumption;
  const realConsumptionValue = totalResult[METRIC_KEY.TURNOVER].foodCost.realConsumption;

  const theoreticalConsumptionTot = totalResult[METRIC_KEY.UNIT].theoreticalConsumption;
  const theoreticalConsumptionValue = totalResult[METRIC_KEY.TURNOVER].theoreticalConsumption;

  // Compute realOverTurnover value
  totalResult[METRIC_KEY.TURNOVER].realOverTurnover =
    turnoverByStoreIdTotal && realConsumptionValue
      ? ratio(realConsumptionValue, turnoverByStoreIdTotal) * 100
      : null;

  totalResult[METRIC_KEY.UNIT].realOverTurnover =
    turnoverByStoreIdTotal && realConsumptionTot
      ? ratio(realConsumptionTot, turnoverByStoreIdTotal) * 100
      : null;

  // Compute relativeRealFromTheorical value (100% when real but no theoretical)
  totalResult[METRIC_KEY.TURNOVER].relativeRealFromTheorical = relativeRealFromTheoreticalRatio(
    totalResult[METRIC_KEY.TURNOVER].foodCost.gapFromTheoretical,
    theoreticalConsumptionValue,
  );
  totalResult[METRIC_KEY.UNIT].relativeRealFromTheorical = relativeRealFromTheoreticalRatio(
    totalResult[METRIC_KEY.UNIT].foodCost.gapFromTheoretical,
    theoreticalConsumptionTot,
  );

  const formattedTotalRow = formattedMetrics.reduce((result, { key }) => {
    result[key] = {
      ...totalResult[key],
      name: i18next.t('GENERAL.TOTAL').toUpperCase(),
      isFixed: true,
    };

    return result;
  }, {});

  return formattedTotalRow;
};

export default {
  fetchAnalytics,
  fetchLocationsByAccountId,
  fetchInventoryDateOfStoreByDates,
};
